/**
 * Schémas zod du **contenu de jeu** — source de vérité unique des types de contenu.
 *
 * Chaque type de `src/content/*.ts` est dérivé du schéma correspondant par `z.infer` :
 * il n'existe aucune duplication type/schéma. Modifier une structure de jeu se fait **ici**,
 * et le type suit automatiquement (DECISIONS §6).
 *
 * Tous les objets de contenu sont `.strict()` : une faute de frappe dans un JSON de
 * `packages/content/data/` (ex. `degat` au lieu de `degats`) est une erreur de validation,
 * pas un champ silencieusement ignoré.
 */
import { z } from 'zod';
// Messages d'erreur en français pour tout le package (effet de bord assumé et documenté).
import './zod-fr.js';
// ---------------------------------------------------------------------------------------------
// Énumérations du domaine
// ---------------------------------------------------------------------------------------------
export const elementSchema = z.enum(['feu', 'eau', 'terre', 'air']);
export const archetypeSchema = z.enum(['mono', 'zone', 'ligne', 'chain', 'aura']);
export const rareteSchema = z.enum(['commune', 'rare', 'legendaire', 'mythique']);
export const modeSchema = z.enum(['limite', 'illimite']);
export const typeEnnemiSchema = z.enum(['normal', 'rapide', 'cuirasse', 'volant', 'boss']);
/** Statistiques de tour qu'une carte peut modifier (DSL fermé, DECISIONS §6.5). */
export const statTourSchema = z.enum([
    'degats',
    'portee',
    'cadenceMs',
    'coutTour',
    'coutAmelioration',
    'vitesseProjectile',
]);
/** Champs d'effet élémentaire qu'une carte peut modifier (DSL fermé, DECISIONS §6.5). */
export const champEffetSchema = z.enum(['degats', 'duree', 'facteur', 'cibles']);
/** Cibles d'un bonus économique (DSL fermé, DECISIONS §6.5). */
export const cibleEconomieSchema = z.enum(['eclatsGagnes', 'coutTour', 'coutAmelioration']);
/** Ressources immédiates qu'une carte peut accorder (DSL fermé, DECISIONS §6.5). */
export const cibleRessourceSchema = z.enum(['jokers', 'eclatsRun']);
// ---------------------------------------------------------------------------------------------
// Effets élémentaires (DECISIONS §6.1) — un par élément, valeurs portées par chaque tour
// ---------------------------------------------------------------------------------------------
export const elementEffectSchema = z.discriminatedUnion('element', [
    z
        .object({
        element: z.literal('feu'),
        /** Brûlure : dégâts sur la durée, cumulables, ignorant 50 % de l'armure. */
        burn: z
            .object({
            dureeMs: z.number().int().positive(),
            degatsParTick: z.number().nonnegative(),
            tickMs: z.number().int().positive(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        element: z.literal('eau'),
        /** Ralentissement : réduit la vitesse, rafraîchi à chaque coup (cumul de durée, pas de valeur). */
        slow: z
            .object({
            facteur: z.number().gt(0).max(1),
            dureeMs: z.number().int().positive(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        element: z.literal('terre'),
        /** Étourdissement bref + dégâts bruts ignorant les boucliers. */
        stun: z
            .object({
            dureeMs: z.number().int().positive(),
            immuniteMs: z.number().int().nonnegative(),
        })
            .strict(),
    })
        .strict(),
    z
        .object({
        element: z.literal('air'),
        /** Chaîne : le projectile rebondit sur la cible suivante la plus proche. */
        chain: z
            .object({
            cibles: z.number().int().positive(),
            facteurParSaut: z.number().gt(0).max(1),
            porteeSaut: z.number().positive(),
        })
            .strict(),
    })
        .strict(),
]);
// ---------------------------------------------------------------------------------------------
// DSL d'effets de carte (DECISIONS §6.5) — liste FERMÉE à 6 formes
// ---------------------------------------------------------------------------------------------
/** `+X %` additif sur une statistique de tour, éventuellement filtré par élément/archétype. */
export const effetBonusStatSchema = z
    .object({
    type: z.literal('bonusStat'),
    stat: statTourSchema,
    /** Pourcentage additif : 10 = +10 %. Les valeurs s'additionnent sans plafond (F16). */
    valeur: z.number(),
    element: elementSchema.optional(),
    archetype: archetypeSchema.optional(),
})
    .strict();
/** `+X %` sur un champ d'effet élémentaire (dégâts de brûlure, facteur de ralentissement…). */
export const effetBonusEffetSchema = z
    .object({
    type: z.literal('bonusEffet'),
    element: elementSchema,
    champ: champEffetSchema,
    valeur: z.number(),
})
    .strict();
/** `+X %` sur l'économie de la run. */
export const effetBonusEconomieSchema = z
    .object({
    type: z.literal('bonusEconomie'),
    cible: cibleEconomieSchema,
    valeur: z.number(),
})
    .strict();
/** Ressource immédiate accordée au joueur (jokers ou éclats de run). */
export const effetRessourceSchema = z
    .object({
    type: z.literal('ressource'),
    cible: cibleRessourceSchema,
    /** Pour `jokers`, entier positif ; pour `eclatsRun`, montant d'éclats. */
    valeur: z.number(),
})
    .strict();
/** Modification des PV du parcours. */
export const effetPvParcoursSchema = z
    .object({
    type: z.literal('pvParcours'),
    valeur: z.number().int(),
})
    .strict();
/** Effet autorisé à l'intérieur d'une synergie : seulement les deux formes « bonus ». */
export const effetDeSynergieSchema = z.union([effetBonusStatSchema, effetBonusEffetSchema]);
/** Synergie : n'a d'effet que si la condition est remplie (elements + nombre de tours minimum). */
export const effetSynergieSchema = z
    .object({
    type: z.literal('synergie'),
    condition: z
        .object({
        elements: z.array(elementSchema).min(1).max(4),
        minTours: z.number().int().positive().optional(),
    })
        .strict(),
    effet: effetDeSynergieSchema,
})
    .strict();
/** Union fermée des 6 formes d'effet autorisées. Aucun script, aucune expression libre. */
export const cardEffectSchema = z.union([
    effetBonusStatSchema,
    effetBonusEffetSchema,
    effetBonusEconomieSchema,
    effetRessourceSchema,
    effetPvParcoursSchema,
    effetSynergieSchema,
]);
// ---------------------------------------------------------------------------------------------
// Élément (métadonnées d'affichage ; les paramètres mécaniques vivent dans la tour)
// ---------------------------------------------------------------------------------------------
export const elementDefinitionSchema = z
    .object({
    id: elementSchema,
    nameKey: z.string().min(1),
    descKey: z.string().min(1),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Tour (DECISIONS §6.3)
// ---------------------------------------------------------------------------------------------
export const ameliorationTourSchema = z
    .object({
    /** Coût du niveau N = `coutParNiveau * N`. */
    coutParNiveau: z.number().nonnegative(),
    /** Facteur multiplicatif des dégâts par niveau (ex. 1.35). */
    facteurDegats: z.number().gt(0),
    /** Facteur multiplicatif de la portée par niveau (ex. 1.10). */
    facteurPortee: z.number().gt(0),
    /** Facteur multiplicatif de la cadence par niveau (ex. 0.90 ; plus petit = plus rapide). */
    facteurCadence: z.number().gt(0).max(1),
})
    .strict();
export const tourSchema = z
    .object({
    /** Identifiant immuable, ex. `tour_feu_mono` (DECISIONS §3). */
    id: z.string().regex(/^tour_[a-z0-9_]+$/, 'identifiant de tour attendu : tour_<slug>'),
    element: elementSchema,
    archetype: archetypeSchema,
    nameKey: z.string().min(1),
    descKey: z.string().min(1),
    /** Éclats nécessaires pour poser la tour. */
    cout: z.number().nonnegative(),
    degats: z.number().nonnegative(),
    /** Portée en pixels du canvas 1280×720. */
    portee: z.number().positive(),
    cadenceMs: z.number().int().positive(),
    /** Vitesse du projectile en px/s (ignorée pour l'archétype `aura`). */
    vitesseProjectile: z.number().nonnegative(),
    effet: elementEffectSchema,
    ameliorations: ameliorationTourSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Carte (DECISIONS §6.4)
// ---------------------------------------------------------------------------------------------
export const carteSchema = z
    .object({
    /** Identifiant immuable, ex. `carte_feu_012`. */
    id: z.string().regex(/^carte_[a-z0-9_]+$/, 'identifiant de carte attendu : carte_<slug>'),
    element: z.union([elementSchema, z.literal('neutre')]),
    rarete: rareteSchema,
    nameKey: z.string().min(1),
    descKey: z.string().min(1),
    /** 1 à 2 effets maximum par carte (DECISIONS §6.4). */
    effets: z.array(cardEffectSchema).min(1).max(2),
    /** Étiquettes de synergie, ex. `['brulure', 'zone']`. */
    tags: z.array(z.string().min(1)),
    /** Points de collection, dérivés de la rareté (non dépensables, F15). */
    points: z.number().int().nonnegative(),
    /** Seul mode de cumul autorisé en v1 (F16). */
    cumul: z.literal('additif'),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Ennemi (DECISIONS §6.6)
// ---------------------------------------------------------------------------------------------
export const ennemiSchema = z
    .object({
    id: z.string().regex(/^enn_[a-z0-9_]+$/, 'identifiant d’ennemi attendu : enn_<slug>'),
    type: typeEnnemiSchema,
    pv: z.number().positive(),
    /** Vitesse en px/s. */
    vitesse: z.number().positive(),
    /** Réduction plate des dégâts, entre 0 et 1. */
    armure: z.number().min(0).max(1),
    /** Éclats gagnés quand cet ennemi est tué. */
    recompenseEclats: z.number().nonnegative(),
    /** PV du parcours perdus s'il atteint la sortie. */
    pvParcoursPerdu: z.number().nonnegative(),
    /** Rayon de collision en pixels. */
    radius: z.number().positive(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Améliorations permanentes (DECISIONS §6.9, F18)
// ---------------------------------------------------------------------------------------------
/**
 * Types d'effet d'une amélioration permanente — liste **fermée** de 5 formes.
 *
 * Ces effets sont appliqués **côté serveur** au démarrage d'une run (`etat-run.ts`) : aucune
 * simulation client n'en dépend, et le client ne recalcule jamais le résultat.
 */
export const typeEffetAmeliorationSchema = z.enum([
    'eclatsDepart',
    'jokersDepart',
    'pvParcours',
    'reductionCoutTour',
    'bonusGainEclats',
]);
/** Effet d'une amélioration permanente : union discriminée des 5 formes du §6.9. */
export const effetAmeliorationPermanenteSchema = z.discriminatedUnion('type', [
    /** Éclats de départ supplémentaires, en valeur absolue. */
    z.object({ type: z.literal('eclatsDepart'), valeur: z.number().int().positive() }).strict(),
    /** Jokers supplémentaires au départ de la run, en valeur absolue. */
    z.object({ type: z.literal('jokersDepart'), valeur: z.number().int().positive() }).strict(),
    /** PV du parcours supplémentaires, en valeur absolue. */
    z.object({ type: z.literal('pvParcours'), valeur: z.number().int().positive() }).strict(),
    /** Réduction du coût de pose d'une tour, en pourcentage (0 < valeur ≤ 100). */
    z.object({ type: z.literal('reductionCoutTour'), valeur: z.number().gt(0).max(100) }).strict(),
    /** Bonus sur les éclats gagnés pendant la run, en pourcentage. */
    z.object({ type: z.literal('bonusGainEclats'), valeur: z.number().gt(0) }).strict(),
]);
/**
 * Amélioration permanente du catalogue (`packages/content/data/ameliorations.json`).
 *
 * `couts` porte le coût de passage au niveau N+1 : sa longueur doit valoir `maxNiveau`
 * (un niveau acheté = une entrée). Les identifiants sont **immuables** (DECISIONS §6.9).
 */
export const ameliorationPermanenteSchema = z
    .object({
    /** Identifiant immuable, ex. `eclats_depart` (snake_case, jamais renommé). */
    id: z.string().regex(/^[a-z][a-z0-9_]+$/, 'identifiant d’amélioration attendu : snake_case'),
    nameKey: z.string().min(1),
    descKey: z.string().min(1),
    /** Niveau maximum, 2 à 5 (DECISIONS §6.9). */
    maxNiveau: z.number().int().min(2).max(5),
    /** Coût de chaque niveau : `couts.length === maxNiveau`, entiers strictement positifs. */
    couts: z.array(z.number().int().positive()).min(1),
    effet: effetAmeliorationPermanenteSchema,
})
    .strict()
    .refine((amelioration) => amelioration.couts.length === amelioration.maxNiveau, {
    message: 'couts doit contenir exactement maxNiveau valeurs (une par niveau)',
    path: ['couts'],
});
// ---------------------------------------------------------------------------------------------
// Vague et niveau (DECISIONS §6.6)
// ---------------------------------------------------------------------------------------------
export const groupeVagueSchema = z
    .object({
    ennemiId: z.string().min(1),
    nombre: z.number().int().positive(),
    /** Délai avant l'apparition du premier ennemi du groupe, en ms. */
    delaiMs: z.number().int().nonnegative(),
    /** Intervalle entre deux ennemis du groupe, en ms. */
    intervalleMs: z.number().int().nonnegative(),
})
    .strict();
export const vagueSchema = z
    .object({
    /** Index 0-based, croissant et sans trou dans un niveau. */
    index: z.number().int().nonnegative(),
    boss: z.boolean(),
    groupes: z.array(groupeVagueSchema).min(1),
})
    .strict();
export const pointGrilleSchema = z
    .object({
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
})
    .strict();
export const emplacementSchema = z
    .object({
    id: z.string().regex(/^emp_[a-z0-9_]+$/, 'identifiant d’emplacement attendu : emp_<slug>'),
    x: z.number().int().nonnegative(),
    y: z.number().int().nonnegative(),
})
    .strict();
export const niveauSchema = z
    .object({
    /**
     * Identifiant immuable. Les niveaux de campagne sont numérotés (`niv_001`) ; `niv_illimite`
     * est l'identifiant **réservé** du gabarit du mode illimité (A-25) : c'est lui que
     * `POST /runs` attend avec `mode: 'illimite'`.
     */
    id: z
        .string()
        .regex(/^(niv_[0-9]{3,}|niv_illimite)$/, 'identifiant de niveau attendu : niv_001 (ou niv_illimite)'),
    numero: z.number().int().positive(),
    mode: modeSchema,
    /** Direction artistique associée (change tous les 10 niveaux, F19). */
    da: elementSchema,
    nameKey: z.string().min(1),
    grille: z
        .object({
        colonnes: z.number().int().positive(),
        lignes: z.number().int().positive(),
    })
        .strict(),
    /** Nœuds de grille formant le parcours, segments orthogonaux uniquement. */
    parcours: z.array(pointGrilleSchema).min(2),
    emplacements: z.array(emplacementSchema).min(1),
    vagues: z.array(vagueSchema).min(1),
    pvParcours: z.number().int().positive(),
    /** Override du paramètre global `pv_parcours_defaut` / `eclats_depart`. */
    eclatsDepart: z.number().int().nonnegative().optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Paramètres d'équilibrage (DECISIONS §6.7) — clés en snake_case
// ---------------------------------------------------------------------------------------------
export const balanceSchema = z
    .object({
    pv_parcours_defaut: z.number().int().positive(),
    eclats_depart: z.number().int().nonnegative(),
    jokers_depart: z.number().int().nonnegative(),
    eclats_par_ennemi_defaut: z.number().int().nonnegative(),
    bonus_fin_niveau: z.number().int().nonnegative(),
    /** Probabilité de carte bonus sur une vague de boss (0..1). */
    chance_carte_bonus: z.number().min(0).max(1),
    nb_vagues_limite: z.number().int().positive(),
    nb_vagues_limite_debut: z.number().int().positive(),
    /** Numéro du premier niveau avec une vague de boss. */
    niveau_premier_boss: z.number().int().positive(),
    nb_vagues_illimite: z.number().int().positive(),
    /** Pause maximale entre deux vagues en mode illimité, en ms. */
    pause_illimite_ms: z.number().int().nonnegative(),
    carte_choix_par_vague: z.number().int().positive(),
    cartes_verrouillees_par_tirage: z.number().int().nonnegative(),
    points_rarete: z
        .object({
        commune: z.number().int().nonnegative(),
        rare: z.number().int().nonnegative(),
        legendaire: z.number().int().nonnegative(),
        mythique: z.number().int().nonnegative(),
    })
        .strict(),
    multiplicateur_difficulte_illimite: z.number().gt(1),
    /** Cartes communes reçues à l'inscription (identifiants de `cartes.json`). */
    deck_depart: z.array(z.string().min(1)).min(1),
    // -----------------------------------------------------------------------------------------
    // Géométrie des archétypes de tours (DECISIONS §1.4 A-29)
    //
    // `tourSchema` est `.strict()` et ne porte aucun paramètre propre à `zone` / `ligne` /
    // `chain` : la **géométrie** vient de l'archétype, l'**effet de statut** vient de l'élément.
    // Ces six valeurs sont donc des paramètres d'équilibrage globaux, réglables en back-office
    // (F24) et publiées par le manifeste — aucune n'est écrite en dur dans le code (§7 pt 13).
    // -----------------------------------------------------------------------------------------
    /** Rayon de la zone d'effet circulaire de l'archétype `zone`, en pixels du canvas 1280×720. */
    zone_rayon_px: z.number().positive(),
    /** Part des dégâts d'une tour `zone` appliquée aux cibles **autres** que la cible visée. */
    zone_facteur_secondaire: z.number().gt(0).max(1),
    /** Facteur de dégâts cumulatif par cible supplémentaire traversée (archétype `ligne`). */
    ligne_facteur_chute: z.number().gt(0).max(1),
    /** Nombre **total** de cibles d'une chaîne, cible initiale comprise (entier ≥ 1). */
    chain_cibles_max: z.number().int().min(1),
    /** Distance maximale, en pixels, entre deux cibles successives d'une chaîne. */
    chain_portee_saut_px: z.number().positive(),
    /** Facteur de dégâts cumulatif appliqué à chaque saut d'une chaîne. */
    chain_facteur_chute: z.number().gt(0).max(1),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Bundle complet
// ---------------------------------------------------------------------------------------------
/**
 * Bundle de contenu validé, tel que servi par l'API (`/content/manifest` en expose le
 * sous-ensemble public). Les fichiers de `packages/content/data/` sont la source de vérité.
 */
export const contenuBundleSchema = z
    .object({
    elements: z.array(elementDefinitionSchema).length(4),
    tours: z.array(tourSchema).min(1),
    cartes: z.array(carteSchema).min(1),
    ennemis: z.array(ennemiSchema).min(1),
    /** Catalogue des améliorations permanentes (F18) : vide tant que M2-06 n'a pas livré le fichier. */
    ameliorations: z.array(ameliorationPermanenteSchema),
    niveaux: z.array(niveauSchema).min(1),
    balance: balanceSchema,
})
    .strict();
/** Catalogues i18n du contenu : `{ fr: {...}, en: {...} }` (DECISIONS §9.1). */
export const cataloguesI18nSchema = z
    .object({
    fr: z.record(z.unknown()),
    en: z.record(z.unknown()),
})
    .strict();
/** Entrée brute de `validateContentBundle` : JSON non validé + catalogues de langue. */
export const contenuBrutSchema = z
    .object({
    elements: z.unknown(),
    tours: z.unknown(),
    cartes: z.unknown(),
    ennemis: z.unknown(),
    /** Section **facultative** tant que M2-06 n'a pas livré `ameliorations.json`. */
    ameliorations: z.unknown().optional(),
    niveaux: z.unknown(),
    balance: z.unknown(),
    i18n: cataloguesI18nSchema,
})
    .strict();
//# sourceMappingURL=schemas.js.map